import React from "react"
import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import * as _ from "lodash"
import { Modal, Row, Col, Skeleton, Typography, theme, notification } from "antd"
import "remixicon/fonts/remixicon.css"

import { updateAuthState } from "../../../data/store/authSlice"
import {
  LOCAL_STORAGE,
  ROUTE_PATH,
  THEME_MODE,
} from "../../../data/configs/constants"
import { setDataInLocalStorage } from "../../../data/utils"
import { IMG_LOGO_DARK, IMG_LOGO_LIGHT } from "../../../data/assets"
import Notify, { notify } from "../Notify"

const CANCEL_COUNTDOWN = 5

const EnterpriseAuthentication = () => {
  const dashboardSmallLogo = useSelector((state) =>
    _.get(
      state,
      "workspaceSettingsDetails.domainConfig.branding.logoUrlSmall",
      null
    )
  )
  const organizationLabel = useSelector((state) =>
    _.get(
      state,
      "workspaceSettingsDetails.domainConfig.branding.organizationLabel",
      ""
    )
  )
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { token: themeConfig } = theme.useToken()
  const queryParams = new URLSearchParams(window.location.search)
  const timeToCancel = queryParams.get("timeToCancel")
  const totalTime = Number(timeToCancel) || CANCEL_COUNTDOWN
  const [timeLeft, setTimeLeft] = React.useState(totalTime)

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft((prev) => prev - 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (timeLeft === -1) {
      navigate(ROUTE_PATH.DASHBOARD)
    }
  }, [timeLeft, navigate])

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    if (queryParams.has("token") && queryParams.has("userId")) {
      const token = queryParams.get("token")
      const userId = queryParams.get("userId")
      const isLoggedIn = true
      dispatch(updateAuthState({ token, userId, isLoggedIn }))
      setDataInLocalStorage(LOCAL_STORAGE.TOKEN, token)
      setDataInLocalStorage(LOCAL_STORAGE.USER_ID, userId)
      setDataInLocalStorage(LOCAL_STORAGE.IS_LOGGED_IN, isLoggedIn)
      setDataInLocalStorage(LOCAL_STORAGE.IS_OLD_USER, true)
    } else {
      notify.error({ message: "Missing auth token" })
      navigate(ROUTE_PATH.LOGIN)
    }
  }, [dispatch, navigate])

  const handleDashboardSwitch = () => {
    const queryParams = new URLSearchParams(window.location.search)
    const token = queryParams.get("token")
    const userId = queryParams.get("userId")
    var domainUrl = `${process.env.REACT_APP_DASHBOARD_URL}${ROUTE_PATH.VERIFY_GOOGLE_AUTH}?token=${token}&userId=${userId}`
    window.open(domainUrl, "_self")
  }

  return (<>
    <Notify />
    <Modal
      open
      closable={false}
      footer={null}
      width={600}
      styles={{
        content: {
          padding: 24,
          borderRadius: 8,
        },
      }}
    >
      <Row gutter={[16, 16]} justify="center" align="middle">
        <Col>
          <div style={{ display: "flex", alignItems: "center" }}>
            <img
              style={{
                borderRadius: "50%",
                height: 80,
                width: 80,
                border: `solid 8px ${themeConfig.colorBgContainer}`,
                transform: "translateX(10%)",
              }}
              src={
                themeConfig.mode === THEME_MODE.DARK
                  ? IMG_LOGO_DARK
                  : IMG_LOGO_LIGHT
              }
              alt="logo"
            />
            {dashboardSmallLogo ? (
              <img
                style={{
                  borderRadius: "50%",
                  height: 80,
                  width: 80,
                  border: `solid 8px ${themeConfig.colorBgContainer
                    }`,
                  transform: "translateX(-10%)",
                }}
                src={dashboardSmallLogo}
                alt="logo"
              />
            ) : (
              <Skeleton.Avatar
                active
                size={80}
                style={{ display: "inline-block" }}
              />
            )}
          </div>
        </Col>
        <Col span={24} style={{ marginBottom: 32 }}>
          <Typography.Title
            level={5}
            style={{ textAlign: "center", marginBottom: 16 }}
          >
            Hold Tight! 🚀
          </Typography.Title>
          <Typography.Text style={{ textAlign: "center", display: "block" }}>
            You're about to be whisked away to your enterprise dashboard{" "}
            <Typography.Text strong type="primary">
              {organizationLabel}
            </Typography.Text>
          </Typography.Text>
        </Col>
        <Col span={24}>
          <ButtonWithPercentage
            value={(1 - timeLeft / totalTime) * 100}
            color="error"
            onClick={handleDashboardSwitch}
          >
            Cancel and go back
          </ButtonWithPercentage>
        </Col>
      </Row>
    </Modal>
  </>
  )
}

const ButtonWithPercentage = ({
  value = 0,
  color = "primary",
  children,
  onClick,
}) => {
  const { token } = theme.useToken()
  return (
    <div
      onClick={onClick}
      style={{
        position: "relative",
        border: `1px solid ${token[color === "error" ? "colorError" : "colorPrimary"]
          }`,
        padding: "8px 16px",
        borderRadius: token.borderRadius,
        backgroundColor: "transparent",
        overflow: "hidden",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: `${value}%`,
          height: "100%",
          backgroundColor:
            token[color === "error" ? "colorErrorBg" : "colorPrimaryBg"],
          transition: "width 1s linear",
        }}
      />
      <Typography.Text
        type={color === "error" ? "danger" : "primary"}
        strong
        style={{
          textAlign: "center",
          display: "block",
          pointerEvents: "none",
        }}
      >
        {children}
      </Typography.Text>
    </div>
  )
}

export default EnterpriseAuthentication
